import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// d.ts
import { IDataController, DCRecord } from "@/@types/lib/dataController"

// Custom Components
import ModelTable from "@/ui/Table/ModelTable";
import DialogContext from "@/ui/DialogContext/DialogContext";
import UserContext from "@/components/UserContext/UserContext";

import dataController from "@/lib/dataController";
import TableButtonView from "@/ui/Table/ActionButtons/TableButtonView";
import TableButtonEdit from "@/ui/Table/ActionButtons/TableButtonEdit";

// Types
import { DialogContextType, IDialogProps } from "@/@types/ui/DialogContext";
import { IModel } from "@/@types/models/model";
import { DialogFormGenerateFunction } from "./DialogFormGenerateFunctionType";

// TODO:
// subModels cleanup: they are added to the project because Table.jsx can not function without them

interface IDataCentricSecondaryTable {
  selectedPrimaryRecordId: number;
  onRecordSelect: (id: number) => void;
  onLoad?: (records: Array<DCRecord>) => void;
  view: string | undefined;
  onRowAction: (action: string, id: number, record?: { [key: string]: any }) => void;

  model: IModel;
  titleToken: string;

  baseRecordPath: string;
  childRecordRelativePath: string;

  allowAdd?: boolean;
  addLabel?: string;
  allowView?: boolean;
  allowEdit?: boolean;
  dialogComponent?: React.FunctionComponent<IDialogProps>
  dialogForm?: string | DialogFormGenerateFunction;
  dialogsUseCombinedPaths?: boolean;

  triggerRefresh: boolean
  setTriggerRefresh: (state: boolean) => void
}

function DataCentricSecondaryTable(props: IDataCentricSecondaryTable) {


  const dialogContext = useContext<DialogContextType>(DialogContext);
  const userContext = useContext(UserContext);
  const { t } = useTranslation();
  const { onRecordSelect, onLoad, onRowAction } = props;
  const { model, titleToken, baseRecordPath, childRecordRelativePath, view, selectedPrimaryRecordId, triggerRefresh, setTriggerRefresh } = props;
  const { allowAdd, addLabel, allowView, allowEdit, dialogComponent, dialogForm, dialogsUseCombinedPaths } = props;

  const urlCombinedPath = `${baseRecordPath}/${selectedPrimaryRecordId}/${childRecordRelativePath}`
  const dc: IDataController = new dataController(model, dialogsUseCombinedPaths ? urlCombinedPath : undefined);


  const [records, setRecords] = useState<DCRecord[]>([]);

  useEffect(() => {
    if (selectedPrimaryRecordId !== 0) {
      refreshRecords();
    } else {
      setRecords([]);
    }
  }, [selectedPrimaryRecordId]);

  useEffect(()=>{
    if (triggerRefresh) {
      setTriggerRefresh(false);
      if (selectedPrimaryRecordId !== 0) {
        refreshRecords();
      }
      
    }
  },[triggerRefresh])


  const refreshRecords = () => {
    const path = `${baseRecordPath}/${selectedPrimaryRecordId}/${childRecordRelativePath}`;
    dc.GetData(path).then((resp) => {
      if (resp.success && Array.isArray(resp.data)) {

        setRecords(resp.data);
        if (onLoad) {
          onLoad(resp.data);
        }
      };
    }).catch((resp) => {
      setRecords([]);
    });
  };

  const handleView = (evt: React.SyntheticEvent, sel: string[]) => {
    const selId: number = parseInt(sel[0], 10);
    const record: DCRecord = records[selId];

    const form = typeof dialogForm === "function" ? dialogForm(record) : dialogForm;

    if (dialogComponent && record?.id) {
      dialogContext.showDialog(dialogComponent, {
        dc: dc,
        mode: "view",
        form: form,
        recordId: record.id,
        onClose: () => {handleDialogClose({dataChanged: true, action: "view", selId: selId})},
      });

    }
  };

  const handleEdit = (evt: React.SyntheticEvent, sel: string[]) => {
    const selId: number = parseInt(sel[0], 10);
    const record: DCRecord = records[selId];


    const form = typeof dialogForm === "function" ? dialogForm(record) : dialogForm;
    
    if (dialogComponent && record?.id) {
      dialogContext.showDialog(dialogComponent, {
        dc: dc,
        mode: "update",
        form: form,
        recordId: record.id,
        onClose: () => {handleDialogClose({dataChanged: true, action: "update", selId: selId})},
      });
    }
  }

  const handleAdd = () => {
    if (dialogComponent) {
      dialogContext.showDialog(dialogComponent, {
        dc: dc,
        mode: "insert",
        form: "insert",
        onClose: () => {handleDialogClose({dataChanged: true, action: "insert"})},
      });
    }
  }

  const handleDialogClose = (result: { dataChanged: boolean; action: string, selId?: number }) => {
    if (result.dataChanged) {
      refreshRecords();
    }
  };


  return (
    <ModelTable
      tableName={model.title}
      title={t(titleToken)}
      records={records}
      initialPageSize={5}
      dc={dc}
      allowSelection="one"
      allowFilter
      allowExport
      allowAdd={allowAdd}
      handleAdd={handleAdd}
      addLabel={addLabel || t("buttons.new")}
      onRowAction={onRowAction}
      allowRowAction={() => true }
      onRowClick={(id: number) => onRecordSelect(id)}
      viewName={view}
      rowSelectedCustom={selectedPrimaryRecordId}
    >
      {allowEdit ?
        <TableButtonEdit variant="outlined" onClick={handleEdit} />
        : null}
      {allowView ?
        <TableButtonView variant="contained" onClick={handleView} />
        : null}
    </ModelTable>
  );
}

export default DataCentricSecondaryTable;
